/* eslint-disable no-restricted-syntax */
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { graphqlClient } from "@/graphql/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const RESEND_MUTATION = `
  mutation { sendVerificationEmail { success error } }
`;

interface MutationResult {
  success: boolean;
  error?: string | null;
}

export function isVerificationError(error: string): boolean {
  return /verify your email/i.test(error);
}

export function EmailVerificationBanner() {
  const { user, refetch } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const resendMutation = useMutation({
    mutationFn: () =>
      graphqlClient.request<{ sendVerificationEmail: MutationResult }>(RESEND_MUTATION),
  });

  // Poll for verification status every 5 seconds while banner is visible
  useEffect(() => {
    if (!user || user.emailVerified || dismissed) return;
    const id = setInterval(() => {
      void refetch();
    }, 5000);
    return () => {
      clearInterval(id);
    };
  }, [user, dismissed, refetch]);

  if (!user || user.emailVerified || dismissed) return null;

  return (
    <div className="bg-primary/5 border-primary/20 flex items-center justify-center gap-3 border-b px-4 py-2 text-sm">
      <span>
        Please verify your email to unlock full access.{" "}
        <button
          type="button"
          onClick={() => {
            resendMutation.mutate();
          }}
          disabled={resendMutation.isPending || resendMutation.isSuccess}
          className="text-primary hover:text-primary/80 font-medium underline underline-offset-4"
        >
          {resendMutation.isSuccess ? "Sent!" : "Resend link"}
        </button>
      </span>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
        }}
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function EmailVerificationPrompt({ message }: { message: string }) {
  const resendMutation = useMutation({
    mutationFn: () =>
      graphqlClient.request<{ sendVerificationEmail: MutationResult }>(RESEND_MUTATION),
  });

  return (
    <div className="border-border bg-muted/50 space-y-3 rounded-lg border p-4">
      <p className="text-sm font-medium">{message}</p>
      <p className="text-muted-foreground text-sm">
        Check your inbox for a verification link, or request a new one below.
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          resendMutation.mutate();
        }}
        disabled={resendMutation.isPending || resendMutation.isSuccess}
      >
        {resendMutation.isSuccess ? "Email sent" : "Resend verification email"}
      </Button>
    </div>
  );
}
